import { useEffect } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface Article {
  id: string;
  title: string;
  description: string;
  content: string;
  category: 'nutrition' | 'safety' | 'development' | 'planning' | 'general';
  image_url?: string;
  min_age: number;
  max_age: number;
  is_premium: boolean;
  tags: string[];
  content_type?: string;
  status?: string;
  reading_time?: number;
  created_at: string;
  updated_at: string;
}

const fetchArticlesFromDB = async (): Promise<Article[]> => {
  const { data, error } = await supabase
    .from('articles')
    .select('*')
    .eq('status', 'published')
    .order('created_at', { ascending: false });

  if (error) throw error;

  return (data || []).map(item => ({
    ...item,
    category: item.category as Article['category'],
  }));
};

export function useArticles() {
  const queryClient = useQueryClient();

  const { data: articles = [], isLoading, error } = useQuery({
    queryKey: ['articles'],
    queryFn: fetchArticlesFromDB,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * Math.pow(2, attempt), 5000),
    placeholderData: keepPreviousData,
  });

  // Realtime subscription — invalidate cache silently
  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout>;

    const channel = supabase
      .channel(`articles-changes-rq-${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'articles' },
        () => {
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: ['articles'] });
          }, 500);
        }
      )
      .subscribe();

    return () => {
      clearTimeout(debounceTimer);
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  const getArticlesByCategory = (category: string) => {
    return articles.filter(article => article.category === category);
  };

  const getArticlesForAge = (ageInMonths: number) => {
    return articles.filter(article =>
      ageInMonths >= article.min_age && ageInMonths <= article.max_age
    );
  };

  const searchArticles = (query: string) => {
    if (!query.trim()) return articles;
    const searchTerm = query.toLowerCase();
    return articles.filter(article =>
      article.title.toLowerCase().includes(searchTerm) ||
      article.description.toLowerCase().includes(searchTerm) ||
      article.tags.some(tag => tag.toLowerCase().includes(searchTerm))
    );
  };

  return {
    articles,
    isLoading,
    error: error ? (error instanceof Error ? error.message : 'An error occurred') : null,
    refetch: () => queryClient.invalidateQueries({ queryKey: ['articles'] }),
    getArticlesByCategory,
    getArticlesForAge,
    searchArticles,
  };
}
